import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Flex,
  useColorModeValue,
  Stack,
  Text,
  //VStack,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse, faUserAlt, faGift, faStar} from "@fortawesome/free-solid-svg-icons"; 
import { useTranslation } from "react-i18next";

const MenuItem = ({ icon, label, onClick, isSelected }) => {
  const menuBgColor = useColorModeValue("teal.600", "teal.200");
  const textColor = useColorModeValue("gray.600", "gray.200");

  return (
    <Box
      w={{ base: "70px", sm: "90px"}}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      cursor="pointer"
      onClick={onClick}
      color={isSelected ? menuBgColor : textColor}
      borderTop={isSelected ? "4px solid" : "none"}
      borderTopColor={menuBgColor}
      paddingTop="8px"
      transition="all 0.2s ease"
      _hover={{
        color: menuBgColor,
        transform: "translateY(-2px)",
      }}
    >
      {/* Render FontAwesome icons correctly */}
      {typeof icon === "object" && icon.iconName ? (
        <FontAwesomeIcon icon={icon} size="lg" />
      ) : (
        // If it's an SVG component, render it directly
        icon
      )}

      <Text fontSize="xs" mt={1}>{label}</Text>
    </Box>
  );
};

const UserHeader = ({ userInfo: propUserInfo }) => {
  const { userInfo: reduxUserInfo } = useSelector((state) => state.auth);
  const userInfo = propUserInfo || reduxUserInfo;
  const navigate = useNavigate();
  const location = useLocation();
  const bgColorDark = useColorModeValue("gray.50", "gray.700");
  const [isSticky, setIsSticky] = useState(false);
  const { t } = useTranslation();


  const selectedTab = useMemo(() => {
    if (location.pathname === "/") return "home";
    if (location.pathname.startsWith("/point")) return "points";
    if (location.pathname.startsWith("/scan")) return "scan";
    if (location.pathname.startsWith("/reward")) return "rewards";
    if (location.pathname === "/profile") return "profile";
    return "home";
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = useCallback(() => navigate("/"), [navigate]);
  const handlePointsClick = useCallback(() => navigate(`/point/${userInfo._id}`), [navigate, userInfo]);
  const handleRewardsClick = useCallback(() => navigate(`/reward/${userInfo._id}`), [navigate, userInfo]);
  const handleProfileClick = useCallback(() => navigate("/profile"), [navigate]);
  const handleScanClick = useCallback(() => navigate("/scan-me"), [navigate]);

  return (
    <Flex
      maxW="lg"
      justify="center"
      align="flex-end"
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={10}
      bg={bgColorDark}
      py={4}
      boxShadow={isSticky ? "md" : "none"}
      transition="box-shadow 0.1s ease, transform 0.1s ease"
      mx="auto"
      borderTopWidth={1}
      height="70px"
    >
      {userInfo && (
        <Stack direction="row" align="flex-end" position="relative" spacing={{ base: 2, sm: 4 }}>
          <MenuItem icon={faHouse} label={t('header_menu.items.home')} onClick={handleClick} isSelected={selectedTab === "home"} />
          <MenuItem icon={faStar} label={t('header_menu.items.points')} onClick={handlePointsClick} isSelected={selectedTab === "points"} />
        
          <MenuItem 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="23" height="23">
                <path fill="currentColor" 
                  d="M8 21H4a1 1 0 0 1-1-1v-4a1 1 0 0 0-2 0v4a3 3 0 0 0 3 3h4a1 1 0 0 0 0-2Zm14-6a1 1 0 0 0-1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 0 0 2h4a3 3 0 0 0 3-3v-4a1 1 0 0 0-1-1ZM20 1h-4a1 1 0 0 0 0 2h4a1 1 0 0 1 1 1v4a1 1 0 0 0 2 0V4a3 3 0 0 0-3-3ZM2 9a1 1 0 0 0 1-1V4a1 1 0 0 1 1-1h4a1 1 0 0 0 0-2H4a3 3 0 0 0-3 3v4a1 1 0 0 0 1 1Zm8-4H6a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1ZM9 9H7V7h2Zm5 2h4a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1Zm1-4h2v2h-2Zm-5 6H6a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1Zm-1 4H7v-2h2Zm5-1a1 1 0 0 0 1-1 1 1 0 0 0 0-2h-1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1Zm4-3a1 1 0 0 0-1 1v3a1 1 0 0 0 0 2h1a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1Zm-4 4a1 1 0 1 0 1 1 1 1 0 0 0-1-1Z"/>
              </svg>
            } 
          label="Scan" onClick={handleScanClick} isSelected={selectedTab === "scan"} />
          <MenuItem icon={faGift} label={t('header_menu.items.rewards')} onClick={handleRewardsClick} isSelected={selectedTab === "rewards"} />
          <MenuItem icon={faUserAlt} label={t('header_menu.items.profile')} onClick={handleProfileClick} isSelected={selectedTab === "profile"} />
        </Stack>
      )}
    </Flex>
  );
};

export default UserHeader;
